import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number, currency = 'ILS'): string {
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(d);
}

export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function generateId(prefix = ''): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return prefix ? `${prefix}-${timestamp}${random}` : `${timestamp}${random}`;
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    planned: 'bg-slate-500',
    released: 'bg-blue-500',
    kitting: 'bg-amber-500',
    in_progress: 'bg-yellow-500',
    testing: 'bg-purple-500',
    completed: 'bg-green-500',
    closed: 'bg-gray-600',
    cancelled: 'bg-red-500',
    draft: 'bg-gray-400',
    active: 'bg-green-500',
    archived: 'bg-gray-500',
    pending: 'bg-yellow-500',
    approved: 'bg-green-500',
    sent: 'bg-blue-500',
    partial: 'bg-orange-500',
    received: 'bg-green-600',
  };

  return colors[status] || 'bg-gray-500';
}

export function getStatusTextColor(status: string): string {
  const colors: Record<string, string> = {
    planned: 'text-slate-700 bg-slate-100',
    released: 'text-blue-700 bg-blue-100',
    kitting: 'text-amber-700 bg-amber-100',
    in_progress: 'text-yellow-700 bg-yellow-100',
    testing: 'text-purple-700 bg-purple-100',
    completed: 'text-green-700 bg-green-100',
    closed: 'text-gray-700 bg-gray-100',
    cancelled: 'text-red-700 bg-red-100',
    draft: 'text-gray-700 bg-gray-100',
    active: 'text-green-700 bg-green-100',
    archived: 'text-gray-700 bg-gray-100',
    pending: 'text-yellow-700 bg-yellow-100',
    approved: 'text-green-700 bg-green-100',
  };

  return colors[status] || 'text-gray-700 bg-gray-100';
}

export function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return `${str.substring(0, length)}...`;
}
